import { FilterType } from 'src/app/components/lib/wizard/filters';

import type { ProductResource } from './product-matching';

/**
 * Helper to create a product filter for requirements or rewards.
 *
 * Algorithm:
 * 1. Validate input array is not null/undefined
 * 2. Filter out products with missing required fields:
 *    - ProductId must not be null/undefined
 *    - ProductName must be present
 * 3. Map to filter resource format:
 *    - resourceId: Convert ProductId to number (handles string/number type variance)
 *    - resourceName: Use ProductName as-is
 * 4. Return filter object with selectionType and resources array
 *
 * @param products - Array of product resources to include in filter
 * @param selectionType - Filter type (INCLUSIONARY, EXCLUSIONARY, or NONE)
 * @returns Filter object ready for use in requirement or reward filters
 *
 * @example
 * // Input: 50 products (2 with null ProductId)
 * // Output: Filter with 48 valid product resources
 * const productFilter = createProductFilter(products, FilterType.INCLUSIONARY);
 *
 * const state = buildDiscountInitialState({
 *   requirements: {
 *     1: { filters: createRequirementFilters({ product: createProductFilter(products) }) }
 *   }
 * });
 */
export function createProductFilter(products: ProductResource[], selectionType: FilterType = FilterType.INCLUSIONARY) {
  if (!products || !Array.isArray(products)) {
    return {
      selectionType,
      resources: [],
    };
  }

  return {
    selectionType,
    resources: products
      .filter((product) => product?.ProductId != null && product?.ProductName)
      .map((product) => ({
        resourceId: Number(product.ProductId),
        resourceName: product.ProductName,
      })),
  };
}

/**
 * Helper to create a brand filter.
 */
export function createBrandFilter(
  brands: Array<{ id: number | string; name: string }>,
  selectionType: FilterType = FilterType.INCLUSIONARY
) {
  if (!brands || !Array.isArray(brands)) {
    return {
      selectionType,
      resources: [],
    };
  }

  return {
    selectionType,
    resources: brands
      .filter((brand) => brand?.id != null && brand?.name)
      .map((brand) => ({
        resourceId: Number(brand.id),
        resourceName: brand.name,
      })),
  };
}

/**
 * Helper to create a category filter.
 */
export function createCategoryFilter(
  categories: Array<{ id: number | string; name: string }>,
  selectionType: FilterType = FilterType.INCLUSIONARY
) {
  if (!categories || !Array.isArray(categories)) {
    return {
      selectionType,
      resources: [],
    };
  }

  return {
    selectionType,
    resources: categories
      .filter((category) => category?.id != null && category?.name)
      .map((category) => ({
        resourceId: Number(category.id),
        resourceName: category.name,
      })),
  };
}

/**
 * Helper to create a generic filter for any resource type.
 * This is the most flexible filter creation function, working with any object shape.
 *
 * Algorithm:
 * 1. Validate input array is not null/undefined
 * 2. Filter out resources with missing required fields:
 *    - Value at idKey must not be null/undefined
 *    - Value at nameKey must not be null/undefined
 * 3. Map to filter resource format using dynamic key access:
 *    - resourceId: Convert resource[idKey] to number
 *    - resourceName: Convert resource[nameKey] to string
 * 4. Return filter object with selectionType and resources array
 *
 * @template T - Type of resource objects being filtered
 * @param resources - Array of resources to include in filter
 * @param idKey - Property name to use as resourceId
 * @param nameKey - Property name to use as resourceName
 * @param selectionType - Filter type (INCLUSIONARY, EXCLUSIONARY, or NONE)
 * @returns Filter object ready for use in requirement or reward filters
 *
 * @example
 * // Create strain filter from strain objects
 * const strainFilter = createResourceFilter(strains, 'StrainId', 'StrainName');
 *
 * @example
 * // Create vendor filter with custom keys
 * const vendorFilter = createResourceFilter(vendors, 'vendorCode', 'vendorTitle');
 */
export function createResourceFilter<T>(
  resources: T[],
  idKey: keyof T,
  nameKey: keyof T,
  selectionType: FilterType = FilterType.INCLUSIONARY
) {
  if (!resources || !Array.isArray(resources)) {
    return {
      selectionType,
      resources: [],
    };
  }

  return {
    selectionType,
    resources: resources
      .filter((resource) => resource?.[idKey] != null && resource?.[nameKey] != null)
      .map((resource) => ({
        resourceId: Number(resource[idKey]),
        resourceName: String(resource[nameKey]),
      })),
  };
}
